// src/components/gamejam/GameJamGrid.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';

export default function GameJamGrid() {
  const [gamesByYear, setGamesByYear] = useState({});
  const [expandedYears, setExpandedYears] = useState({});
  const [bannerUrls, setBannerUrls] = useState({});

  const awardLabels = ['대상', '최우수상', '우수상', '우수상'];
  const trophyIcons = ['🥇', '🥈', '🥉', '🥉'];

  // 1) Firestore에서 gamejam 데이터 불러와서 bannerPath로 저장
  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'data', 'gamejam'));
      if (!snap.exists()) return;
      const data = snap.data();
      const result = {};

      for (const [yearKey, rawIds] of Object.entries(data)) {
        if (!Array.isArray(rawIds)) continue;
        const arr = [];

        for (let i = 0; i < 4; i++) {
          const id = rawIds[i] ?? '';
          if (!id) {
            arr.push({ id: '', title: '데이터 없음', description: '', bannerPath: '' });
            continue;
          }
          const gameSnap = await getDoc(doc(db, 'games', id));
          if (!gameSnap.exists()) {
            arr.push({ id, title: '데이터 없음', description: '', bannerPath: '' });
            continue;
          }
          const g = gameSnap.data();
          const stored = g.bannerImageUrl || '';
          const bannerPath = stored.startsWith('http')
            ? stored
            : (stored || `games/${id}/${id}_banner`);

          arr.push({
            id,
            title: g.title,
            description: g.shortDescription || '',
            bannerPath,
          });
        }

        result[yearKey] = arr;
      }

      setGamesByYear(result);
    })();
  }, []);

  // 2) bannerPath에 따라 Storage에서 URL 가져와 bannerUrls에 저장 + localStorage 캐싱
  useEffect(() => {
    const storage = getStorage();

    Object.values(gamesByYear).flat().forEach(({ id, bannerPath }) => {
      if (!id || bannerUrls[id]) return;

      if (bannerPath.startsWith('http')) {
        setBannerUrls(prev => ({ ...prev, [id]: bannerPath }));
        try { localStorage.setItem(`gameJamBanner_${id}`, bannerPath); } catch {}
        return;
      }

      const cached = localStorage.getItem(`gameJamBanner_${id}`);
      if (cached) {
        setBannerUrls(prev => ({ ...prev, [id]: cached }));
        return;
      }

      getDownloadURL(storageRef(storage, bannerPath))
        .then(url => {
          setBannerUrls(prev => ({ ...prev, [id]: url }));
          try { localStorage.setItem(`gameJamBanner_${id}`, url); } catch {}
        })
        .catch(() => {});
    });
  }, [gamesByYear, bannerUrls]);

  const toggle = yearKey => {
    setExpandedYears(prev => ({ ...prev, [yearKey]: !prev[yearKey] }));
  };

  return (
    <div className="container mx-auto px-20 py-8 space-y-16">
      {/* 상단 헤더 */}
      <div className="text-center mb-24">
        <img src="/images/logo/logo_gamejam1.png" alt="MYME GameJam 로고" className="mx-auto h-40" />
        <p className="mt-4 text-xl text-white font-bold">
          MYME와 여러분이 함께 만드는 새로운 경험
        </p>
      </div>

      {/* 연도별 수상작 그리드 */}
      {Object.entries(gamesByYear)
        .sort(([aKey],[bKey]) =>
          parseInt(bKey.replace('gamejam',''), 10) - parseInt(aKey.replace('gamejam',''), 10)
        )
        .map(([yearKey, games]) => {
          const year = yearKey.replace('gamejam', '');
          const winner = games[0];
          const nominees = games.slice(1);
          const isExpanded = !!expandedYears[yearKey];
          const winnerUrl = winner.id ? bannerUrls[winner.id] : '';

          return (
            <section key={yearKey} className="max-w-6xl mx-auto bg-[#1f1f1f] p-6 rounded-xl shadow-lg">
              {/* header with title and '더보기' */}
              <div className="flex items-center justify-between text-white p-4">
                <h2 className="text-2xl font-bold">
                  {trophyIcons[0]} {year}년 <span className="text-red-500">MYME</span> 게임잼 어워드 수상작
                </h2>
                <Link
                  to="/games"
                  state={{ filterCategory: 'gameJam', filterValue: `게임잼 ${year}` }}
                  className="text-base text-gray-300 hover:text-white font-bold"
                >
                  {year} 참가작 &gt;
                </Link>
              </div>

              {/* 대상 */}
              <div className="flex flex-col md:flex-row md:space-x-12 px-4 py-8">
                <Link
                  to={winner.id ? `/games/detail/${winner.id}` : '#'}
                  className="relative w-full md:w-1/2 aspect-banner bg-black rounded-xl overflow-hidden border-2 border-neutral-300"
                >
                  {winner.id && winnerUrl
                    ? <img src={winnerUrl} alt={winner.title} className="object-cover w-full h-full" loading="lazy" />
                    : <div className="flex items-center justify-center w-full h-full text-gray-500">데이터 없음</div>
                  }
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 px-3 py-1 rounded-xl flex items-center space-x-1">
                    <span className="text-2xl">{trophyIcons[0]}</span>
                    <span className="text-white font-semibold">{awardLabels[0]}</span>
                  </div>
                </Link>
                <div className="mt-6 md:mt-0 w-full md:w-1/2 flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-semibold text-white">{winner.id ? winner.title : '데이터 없음'}</h3>
                    <p className="text-gray-300">{winner.id ? winner.description : ''}</p>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => toggle(yearKey)}
                      className="text-sm px-3 py-2 bg-neutral-700 text-gray-200 rounded-lg hover:bg-neutral-600"
                    >
                      {isExpanded ? '수상작 숨기기' : '수상작 표시'}
                    </button>
                  </div>
                </div>
              </div>

              {/* 후보작 */}
              {isExpanded && (
                <div className="p-4">
                  <div className="flex justify-center space-x-6">
                    {nominees.map((g, idx) => {
                      const url = g.id ? bannerUrls[g.id] : '';
                      return (
                        <Link
                          key={idx}
                          to={g.id ? `/games/detail/${g.id}` : '#'}
                          className="relative w-1/3 bg-[#2a2a2a] rounded-xl overflow-hidden hover:shadow-lg transition-shadow border-2 border-neutral-300"
                        >
                          <div className="w-full aspect-banner bg-black">
                            {g.id && url
                              ? <img src={url} alt={g.title} className="object-cover w-full h-full" loading="lazy" />
                              : <div className="flex items-center justify-center w-full h-full text-gray-500">데이터 없음</div>
                            }
                          </div>
                          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 px-2 py-1 rounded-xl flex items-center space-x-1">
                            <span className="text-base">{trophyIcons[idx+1]}</span>
                            <span className="text-white font-semibold">{awardLabels[idx+1]}</span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          );
        })}
    </div>
  );
}
